import { useMemo } from 'react';

/**
 * Live phone-frame preview of a blog post, styled to match the Next Health app's
 * BlogDetailScreen (next-health-app/src/screens/BlogDetailScreen.tsx). The body
 * is rendered the same way the app does: HTML as-is, else a small markdown→HTML
 * pass, then the "numbered steps" cleanup. Keep this in sync with that screen.
 */

const NAVY = '#0A2540';

const looksLikeHtml = (text: string) => /<\/?[a-z][\s\S]*>/i.test(text);

const mergeNumberedSteps = (html: string): string => {
  let out = html;
  out = out.replace(
    /<(p|div)(\s[^>]*)?>\s*(\d{1,2})\s*\.?\s*<\/\1>\s*<(p|div)(\s[^>]*)?>([\s\S]*?)<\/\4>/gi,
    (_m, tag, _a1, num, _t2, _a2, body) =>
      `<${tag}><strong>${num}.</strong> ${body}</${tag}>`,
  );
  out = out.replace(
    /(^|>)\s*(\d{1,2})\s*\.?\s*<br\s*\/?>\s*/gi,
    (_m, pre, num) => `${pre}<strong>${num}.</strong> `,
  );
  return out;
};

// Editors emit empty paragraphs (<p></p>, <p><br></p>) for blank lines, which the
// HTML renderer collapses — making the author's spacing disappear. Give each one a
// non-breaking space so it renders as a real blank line (matches BlogDetailScreen).
const preserveBlankLines = (html: string): string =>
  html.replace(/<p([^>]*)>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '<p$1>&nbsp;</p>');

// Content is often HTML with paragraph/line breaks stored as raw \n inside the
// text (one big <p> with \r\n\r\n between paragraphs). HTML collapses those, so it
// runs together. Convert text newlines to <br>, ignoring newlines that just format
// the markup between block tags. Mirrors BlogDetailScreen in next-health-app.
const NL_BLOCKS = 'p|div|h[1-6]|ul|ol|li|hr|blockquote|table|thead|tbody|tr|td|th|br';
const normalizeNewlines = (html: string): string => {
  let out = html.replace(/\r\n?/g, '\n');
  out = out.replace(new RegExp(`\\n+\\s*(?=<\\/?(?:${NL_BLOCKS})\\b)`, 'gi'), '');
  out = out.replace(new RegExp(`(<\\/?(?:${NL_BLOCKS})\\b[^>]*>)\\s*\\n+`, 'gi'), '$1');
  out = out.replace(/\n{2,}/g, '<br /><br />');
  out = out.replace(/\n/g, '<br />');
  return out;
};

const markdownToHtml = (md: string): string => {
  const esc = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const withLinks = esc.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  const withBold = withLinks.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const withItalic = withBold.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  const withHeadings = withItalic
    .replace(/^###\s+(.*)$/gm, '<h3>$1</h3>')
    .replace(/^##\s+(.*)$/gm, '<h2>$1</h2>')
    .replace(/^#\s+(.*)$/gm, '<h1>$1</h1>');
  return withHeadings
    .split(/\n{2,}/)
    .map((block) => {
      const t = block.trim();
      if (!t) return '';
      if (/^<h[1-3]>/.test(t)) return t;
      return `<p>${t.replace(/\n/g, '<br />')}</p>`;
    })
    .join('\n');
};

interface BlogPostPhonePreviewProps {
  title: string;
  subtitle: string;
  body: string;
  heroImageUrl: string | null;
  tags: string[];
  readingTime: number;
}

export function BlogPostPhonePreview({
  title,
  subtitle,
  body,
  heroImageUrl,
  tags,
  readingTime,
}: BlogPostPhonePreviewProps) {
  const bodyHtml = useMemo(() => {
    const raw = (body ?? '').trim();
    if (!raw) return '';
    const html = normalizeNewlines(looksLikeHtml(raw) ? raw : markdownToHtml(raw));
    return preserveBlankLines(mergeNumberedSteps(html));
  }, [body]);

  const subtitleHtml = subtitle && looksLikeHtml(subtitle) ? subtitle : null;
  const meta = readingTime > 0 ? `${readingTime} min read` : '';

  return (
    <div className="select-none">
      <p className="mb-2 text-xs font-medium text-slate-500">Phone preview</p>

      {/* Device frame */}
      <div className="mx-auto w-[320px] rounded-[36px] border-[10px] border-slate-900 bg-slate-900 shadow-xl">
        <div className="relative h-[600px] overflow-hidden rounded-[26px] bg-white">
          {/* Notch */}
          <div className="absolute left-1/2 top-0 z-10 h-5 w-28 -translate-x-1/2 rounded-b-2xl bg-slate-900" />

          {/* Header bar */}
          <div
            className="flex items-center gap-3 px-4 pb-3 pt-7"
            style={{ borderBottom: '1px solid rgba(10,37,64,0.08)' }}
          >
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full text-lg"
              style={{ background: 'rgba(10,37,64,0.06)', color: NAVY }}
            >
              ←
            </div>
            <p
              className="line-clamp-2 text-[18px] font-bold leading-[22px]"
              style={{ color: NAVY, letterSpacing: '-0.3px' }}
            >
              {title || ' '}
            </p>
          </div>

          {/* Scrollable article */}
          <div className="h-[543px] overflow-y-auto p-4">
            {heroImageUrl ? (
              <img
                src={heroImageUrl}
                alt=""
                className="mb-4 h-[180px] w-full rounded-[14px] object-cover"
                style={{ background: '#E5E7EB' }}
              />
            ) : null}

            {tags?.length ? (
              <div className="mb-3 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    style={{ background: 'rgba(10,37,64,0.08)', color: NAVY, letterSpacing: '0.3px' }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}

            <h1 className="text-[24px] leading-[30px]" style={{ color: NAVY, fontWeight: 500 }}>
              {title || 'Post title'}
            </h1>

            {subtitle ? (
              subtitleHtml ? (
                <div
                  className="mt-2 text-[15px] leading-[22px]"
                  style={{ color: '#4B5563' }}
                  dangerouslySetInnerHTML={{ __html: subtitleHtml }}
                />
              ) : (
                <p className="mt-2 text-[15px] leading-[22px]" style={{ color: '#4B5563' }}>
                  {subtitle}
                </p>
              )
            ) : null}

            {meta ? (
              <p className="mb-2 mt-2.5 text-[12px]" style={{ color: '#6B7280' }}>
                {meta}
              </p>
            ) : null}

            {bodyHtml ? (
              <div className="bp-phone-body mt-3" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
            ) : (
              <p className="mt-3 text-[15px] italic" style={{ color: '#9CA3AF' }}>
                Body preview will appear here…
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Scoped styles mirroring the app's htmlTagsStyles + htmlBase */}
      <style>{`
        .bp-phone-body { color: #111827; font-size: 15px; line-height: 24px; }
        .bp-phone-body h1 { font-size: 22px; font-weight: 700; margin: 16px 0 4px; }
        .bp-phone-body h2 { font-size: 18px; font-weight: 700; margin: 14px 0 4px; }
        .bp-phone-body h3 { font-size: 16px; font-weight: 700; margin: 12px 0 4px; }
        .bp-phone-body p { margin: 0 0 12px; }
        .bp-phone-body p:empty::before { content: "\\00a0"; }
        .bp-phone-body ul, .bp-phone-body ol { margin: 4px 0 8px; padding-left: 20px; }
        .bp-phone-body ul { list-style: disc; }
        .bp-phone-body ol { list-style: decimal; }
        .bp-phone-body li { margin-bottom: 4px; }
        .bp-phone-body a { color: ${NAVY}; }
        .bp-phone-body strong { font-weight: 700; }
        .bp-phone-body em { font-style: italic; }
        .bp-phone-body img { border-radius: 8px; margin: 8px 0; max-width: 100%; }
      `}</style>
    </div>
  );
}
